import { Box, Heading, Icon, Image, List, MediaObject, Paragraph } from '@zillow/constellation-10';
import {
  IconHomesFilled,
  IconKeyFilled,
  IconPiggyBankFilled,
  IconShieldCheckmarkFilled,
} from '@zillow/constellation-icons';

import HomeRental from '../../images/HomeRental.svg';

import type { UpsellInfo } from '../../types';

type UpsellBodyProps = {
  upsellInfo: UpsellInfo;
};

const UpsellBody = ({ upsellInfo }: UpsellBodyProps) => {
  const isInsuranceRequired = upsellInfo.isInsuranceRequiredForLease || upsellInfo.isInsuranceRequiredForListing;
  const upsellBodyElements = [
    {
      icon: isInsuranceRequired ? <IconShieldCheckmarkFilled /> : <IconKeyFilled />,
      text: isInsuranceRequired ? 'Get coverage in minutes' : 'Plan for your move',
      paragraph: isInsuranceRequired
        ? 'You can even choose to auto-send proof of coverage to your landlord.'
        : 'Get covered in less time than it takes to find movers.',
    },
    {
      icon: <IconPiggyBankFilled />,
      text: `Popular plans start at ${upsellInfo.monthlyPriceEstimate}/mo`,
      paragraph: `Join renters near ${upsellInfo.zip}, who protect their personal items from events like theft or damage.`,
    },
    {
      icon: <IconHomesFilled />,
      text: 'Protect the things you love',
      paragraph: 'Customize your plan with coverage for high value items, such as your laptop, jewelry, and more.',
    },
  ];

  const getSubheadingText = () => {
    if (upsellInfo.isInsuranceRequiredForLease) {
      return 'Your lease requires insurance';
    } else if (upsellInfo.isInsuranceRequiredForListing) {
      return 'Your landlord requires insurance';
    }

    return 'Get coverage in minutes';
  };

  return (
    <Box data-testid="upsell-modal-upsell-info">
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor:
            '{colors.blue.100}' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
          padding: 'default',
        }}
      >
        <Image alt="Home Rental" src={HomeRental} />
        <Heading level={3} textStyle="heading-sm" css={{ marginBottom: 'tighter' }}>
          Renters insurance
        </Heading>
        <Paragraph css={{ marginBottom: 'default' }}>{getSubheadingText()}</Paragraph>
      </Box>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'looser',
          margin: { base: 'looser', md: '{looser} 0' },
        }}
      >
        <List.Root appearance="none">
          {upsellBodyElements.map((element, index) => (
            <List.Item key={index}>
              <MediaObject media={<Icon size="xl" render={element.icon} />}>
                <Box css={{ marginLeft: 'tighter', maxWidth: '396px' }}>
                  <Paragraph textStyle="body-bold">{element.text}</Paragraph>
                  <Paragraph color="text.subtle">{element.paragraph}</Paragraph>
                </Box>
              </MediaObject>
            </List.Item>
          ))}
        </List.Root>
      </Box>
    </Box>
  );
};

export default UpsellBody;
